import React, {
  useMemo,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { Paper } from '@mui/material';
import { useIntl } from 'react-intl';
import useAPI from '../../api';
import PivotTable from '../../Components/PivotTable';
import ContentError from '../../Components/Error/ContentError';
import { FullSizeCenteredFlexBox } from '../../Components/FlexBox';
import ConfigurationDialog from './ConfigurationDialog';
import ConfigurationsOptions from './ConfigurationsOptions';
import ConfirmDialog from '../../Components/Dialog/ConfirmDialog';

const PivotWrapper = function PivotWrapper({
  data,
  visible,
  slice,
  getMetadata,
  localStorageKey,
  pivotType,
  userRoles,
}) {
  const API = useAPI();
  const intl = useIntl();
  const metadata = useMemo(() => getMetadata(intl), []);
  const pivotTableRef = useRef(null);
  const [pivotConfigurations, setPivotConfigurations] = useState([]);
  const [configOpen, setConfigOpen] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [currentConfiguration, setCurrentConfiguration] = useState(slice);
  const [selectedConfiguration, setSelectedConfiguration] = useState(null);
  const [currentDescription, setCurrentDescription] = useState({
    name: '',
    description: '',
  });

  const handleChangeSelectedConfiguration = (value) => {
    setSelectedConfiguration(value);
  };

  const isSupervisor = Boolean(
    userRoles?.find(
      ({ roleId }) =>
        roleId === 'ADMIN' || roleId === 'SUPERVISOR' || roleId === 'SUPERADMIN'
    )
  );

  const loadPivotConfigurations = () =>
    API.getPivotConfigurations(
      pivotType,
      (res) => {
        setPivotConfigurations(res);
      },
      () => {}
    );

  useEffect(() => {
    loadPivotConfigurations();
  }, []);

  const createPivotConfiguration = (data) => {
    API.createPivotConfiguration(
      { ...data, pivotType },
      (res) => {
        setPivotConfigurations(res);
        setConfigOpen(null);
      },
      () => {}
    );
  };

  const editPivotConfiguration = (data) => {
    API.editPivotConfiguration(
      { ...data, pivotType },
      (res) => {
        setPivotConfigurations(res);
        setConfigOpen(null);
      },
      () => {}
    );
  };
  const deletePivotConfiguration = (id) => {
    API.deletePivotConfiguration(
      id,
      () => {
        loadPivotConfigurations();
        setConfirmDelete(false);
        setSelectedConfiguration(null);
      },
      () => {}
    );
  };

  useEffect(() => {
    if (pivotConfigurations.length > 0) {
      setSelectedConfiguration(
        localStorage.getItem(localStorageKey) &&
          pivotConfigurations?.find(
            ({ id }) =>
              id === parseInt(localStorage.getItem(localStorageKey), 10)
          )
          ? parseInt(localStorage.getItem(localStorageKey), 10)
          : pivotConfigurations?.find(({ id }) => id !== 0)?.id
      );
    }
  }, [pivotConfigurations]);

  useEffect(() => {
    if (!selectedConfiguration) {
      if (selectedConfiguration !== 0) return;

      setConfigOpen('NEW');
      return;
    }
    localStorage.setItem(localStorageKey, selectedConfiguration);
    setCurrentDescription(
      pivotConfigurations?.find(({ id }) => selectedConfiguration === id) || {
        name: '',
        description: '',
      }
    );
    setCurrentConfiguration(
      pivotConfigurations?.find(({ id }) => selectedConfiguration === id)
        ?.jsonStructure
        ? JSON.parse(
            pivotConfigurations?.find(({ id }) => selectedConfiguration === id)
              ?.jsonStructure
          )
        : slice
    );
  }, [selectedConfiguration]);

  const handleChangeCurrentConfiguration = (config) => {
    setCurrentConfiguration(config);
  };

  const buildPersistableConfiguration = () => {
    // Pull the freshest report straight from the engine so that format edits
    // (formatchange) and computed-field edits (datachange) — which don't
    // trigger reportchange — are included in the payload.
    const live = pivotTableRef.current?.getReport?.() || null;
    const base = live || currentConfiguration;
    const sourceType =
      base?.dataSource?.dataSourceType ||
      currentConfiguration?.dataSource?.dataSourceType ||
      'json';
    const firstRow =
      base?.dataSource?.data?.[0] ??
      currentConfiguration?.dataSource?.data?.[0];
    return {
      slice: base?.slice,
      options: base?.options,
      formats: base?.formats,
      calculatedFields: base?.calculatedFields || [],
      dataSource: {
        dataSourceType: sourceType,
        data: firstRow ? [firstRow] : [],
      },
    };
  };

  const handleCreateConfiguration = useCallback(() => {
    createPivotConfiguration({
      ...currentDescription,
      jsonStructure: JSON.stringify(buildPersistableConfiguration()),
    });
  }, [currentConfiguration, currentDescription]);

  const handleEditConfiguration = useCallback(() => {
    editPivotConfiguration({
      id: selectedConfiguration,
      ...currentDescription,
      jsonStructure: JSON.stringify(buildPersistableConfiguration()),
    });
  }, [currentConfiguration, selectedConfiguration, currentDescription]);

  const handleDeleteConfiguration = useCallback(() => {
    deletePivotConfiguration(selectedConfiguration);
  }, [selectedConfiguration]);

  const handleSubmitDescription = (descriptionData) => {
    setCurrentDescription(descriptionData);
    setConfigOpen(null);
  };

  useEffect(() => {
    if (visible) setCurrentConfiguration((prev) => ({ ...prev })); //forces re-render
  }, [visible]);
  return (
    <>
      <ConfirmDialog
        open={confirmDelete}
        onCancel={() => setConfirmDelete(false)}
        onSubmit={handleDeleteConfiguration}
        title="CONFIRM_DELETE_CONFIGURATION"
        question="CONFIRM_DELETE_CONFIGURATION_QUESTION"
        fontSize="1.5rem"
      />
      <ConfigurationDialog
        edit={configOpen === 'EDIT'}
        open={Boolean(configOpen)}
        onClose={() => setConfigOpen(null)}
        onSubmit={handleSubmitDescription}
        defaultData={
          selectedConfiguration &&
          pivotConfigurations?.find(({ id }) => selectedConfiguration === id)
        }
      />
      <Paper
        id="pivot-wrapper"
        elevation={3}
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '18px',
          padding: '16px',
          gap: '8px',
          overflow: 'hidden',
        }}
      >
        <ConfigurationsOptions
          currentDescription={currentDescription}
          pivotConfigurations={pivotConfigurations}
          selectedConfiguration={selectedConfiguration}
          handleChangeSelectedConfiguration={handleChangeSelectedConfiguration}
          onClickEdit={() => setConfigOpen('EDIT')}
          onClickDelete={() => setConfirmDelete(true)}
          isSupervisor={isSupervisor}
          disabled={
            selectedConfiguration === null ||
            (selectedConfiguration === 0 && currentDescription?.name === '')
          }
          onSave={
            selectedConfiguration === 0
              ? handleCreateConfiguration
              : handleEditConfiguration
          }
        />
        {data && data.length > 0 ? (
          <PivotTable
            ref={pivotTableRef}
            visible={visible}
            data={data}
            metadata={metadata}
            handleChangeCurrentConfiguration={handleChangeCurrentConfiguration}
            defaultConfiguration={currentConfiguration}
          />
        ) : (
          <FullSizeCenteredFlexBox justifyContent="center">
            <ContentError text={intl.messages.NO_DATA_AVAILABLE} />
          </FullSizeCenteredFlexBox>
        )}
      </Paper>
    </>
  );
};

PivotWrapper.propTypes = {
  data: PropTypes.object,
  visible: PropTypes.bool,
  slice: PropTypes.object,
  getMetadata: PropTypes.func,
  localStorageKey: PropTypes.string,
};

export default PivotWrapper;
